/**
 * Is the current web host a local development host?
 *
 * `.localhost` is reserved for loopback by RFC 6761, so any host under it is
 * local by definition and cannot be pointed at a real site. That is what lets
 * several local instances run side by side on distinct hostnames
 * (`lane0.dapple.localhost`, `patient-lane2.dapple.localhost`) instead of
 * fighting over a single `localhost`.
 *
 * Note the leading dot: the check is on the reserved TLD, not on a bare "ends
 * with localhost", which a domain like `evil-localhost` could otherwise satisfy.
 *
 * This exists because the same check was open-coded in three places with three
 * different (and two wrong) definitions — see `isDev`, `isClinicApp` and the
 * trial welcome panel. Add new local-host checks here, not inline.
 */
export const isLocalHostname = (hostname: string | undefined): boolean => {
  if (!hostname) return false;

  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
};

/**
 * The current web host, or undefined off-web / before `window` exists.
 */
export const currentHostname = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return window.location?.hostname;
};

/**
 * Is the page being served under a local hostname?
 */
export const isLocalHost = (): boolean => isLocalHostname(currentHostname());

/**
 * Is the page served under the reserved `.localhost` TLD on a *named* host —
 * `lane0.dapple.localhost`, `patient-lane2.dapple.localhost` — rather than
 * bare `localhost` or a loopback IP?
 *
 * The distinction matters because these two local setups put the API in
 * different places:
 *
 *   - A named `.localhost` host is served by the lane edge proxy, which routes
 *     `/dapple-gql*` to the backend and everything else to the web build **on
 *     one origin**. The API is therefore same-origin, always.
 *   - Bare `localhost` is the classic split-port dev setup — webpack on 19006,
 *     the API on 3000 — where same-origin does NOT hold.
 *
 * So this is deliberately narrower than {@link isLocalHost}: it answers "is
 * the API reachable at my own origin", not "am I running locally".
 */
export const isSameOriginLocalHost = (): boolean => {
  const hostname = currentHostname();

  return !!hostname && hostname.endsWith(".localhost");
};
